import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { updateMetaTag } from "@/lib/seo-utils";
import { updateCanonicalTag, getProductionUrl } from "@/lib/canonical-utils";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ScrollProgress } from "@/components/ScrollProgress";

interface LegalSection {
  id: string;
  section_title: string;
  section_content: string;
  display_order: number;
  last_updated: string;
}

const Terms = () => {
  const [sections, setSections] = useState<LegalSection[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Hardcoded SEO for Terms page
    document.title = "Terms of Service | ShoreSight Marine Windows";
    
    const metaDescription = "Review the terms of service for ShoreSight Marine Windows. Learn about our policies, service agreements, and legal terms for marine window maintenance and repair services.";
    
    updateMetaTag("name", "description", metaDescription);
    updateMetaTag("property", "og:title", document.title);
    updateMetaTag("property", "og:description", metaDescription);
    updateMetaTag("property", "og:type", "website");
    updateMetaTag("name", "twitter:card", "summary");
    updateMetaTag("name", "twitter:title", document.title);
    updateMetaTag("name", "twitter:description", metaDescription);
    updateMetaTag("name", "robots", "noindex, follow");

    // Canonical and OG URL
    const canonicalUrl = getProductionUrl("/terms");
    updateCanonicalTag(canonicalUrl);
    updateMetaTag("property", "og:url", canonicalUrl);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch legal sections
      const { data: sectionsData } = await (supabase as any)
        .from("legal_sections")
        .select("*")
        .eq("page_id", "terms")
        .order("display_order");
      
      if (sectionsData && sectionsData.length > 0) {
        setSections(sectionsData);
        // Get the most recent update date
        const dates = sectionsData.map((s: LegalSection) => new Date(s.last_updated));
        const mostRecent = new Date(Math.max(...dates.map(d => d.getTime())));
        setLastUpdated(mostRecent.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }));
      }
      
      setLoading(false);
    };
    
    fetchData();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 py-16 md:py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-40"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-azure-500/10 border border-azure-500/20 mb-6">
              <FileText className="w-8 h-8 text-azure-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Terms of Service
            </h1>
            {lastUpdated && (
              <p className="text-azure-200 text-sm mb-4">
                Last Updated: {lastUpdated}
              </p>
            )}
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Please review our terms of service carefully. By using our services, you agree to these terms and conditions.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main id="main-content" className="flex-1 py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">

            {/* Table of Contents */}
            {sections.length > 0 && (
              <nav className="mb-8 p-8 rounded-xl bg-gradient-to-br from-card to-card/50 border border-border shadow-lg">
                <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Table of Contents
                </h2>
                <ol className="space-y-3">
                  {sections.map((section, index) => (
                    <li key={section.id}>
                      <a
                        href={`#section-${index + 1}`}
                        className="text-primary hover:text-azure-600 hover:underline transition-colors font-medium group flex items-start gap-2"
                      >
                        <span className="text-muted-foreground group-hover:text-primary transition-colors">{index + 1}.</span>
                        <span>{section.section_title}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

          {/* Content Sections */}
          {loading ? (
            <div className="space-y-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="h-6 bg-muted rounded w-1/3 mb-3"></div>
                  <div className="h-4 bg-muted rounded w-full mb-2"></div>
                  <div className="h-4 bg-muted rounded w-5/6"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="prose prose-slate max-w-none">
              {sections.map((section, index) => (
                <section
                  key={section.id}
                  id={`section-${index + 1}`}
                  className="mb-8 scroll-mt-20"
                >
                  <h2 className="text-2xl font-semibold text-foreground mb-4">
                    {index + 1}. {section.section_title}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {section.section_content}
                  </p>
                </section>
              ))}
            </div>
          )}

            {/* Footer CTA */}
            <div className="mt-12 p-8 rounded-xl bg-gradient-to-br from-navy-900 to-navy-800 border border-navy-700 shadow-xl text-center">
              <h2 className="text-2xl font-semibold text-white mb-3">
                Have Questions?
              </h2>
              <p className="text-gray-300 mb-6 max-w-xl mx-auto">
                If you have any questions about our Terms of Service, please don't hesitate to contact us.
              </p>
              <Link to="/#contact">
                <Button size="lg" className="bg-azure-500 hover:bg-azure-600 text-white">
                  Contact Us
                </Button>
              </Link>
            </div>

            {/* Back to Home */}
            <div className="mt-8 text-center">
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  <Home className="w-4 h-4" />
                  Back to Homepage
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;
